// useDevActions Hook
// Extracts dev/debug action handling from Game.tsx for better modularity
// Network operations are routed through EventBus for decoupled architecture.

import React, { useCallback } from 'react';
import { Helix2000Character, CharacterClass } from '../../shared/CharacterTypes';
import { GameScene } from '../scenes/GameScene';
import { EventBus, EVENTS } from '../../shared/EventBus';
import { TextureGenerator } from '../utils/rendering/TextureGenerator';
import { CharacterService } from '../services/CharacterService';
import { spawnFloatingDamageText } from '../rendering/utils/FloatingDamageText';
import { createHelix2000Character } from '../../shared/CharacterClasses';
import { GAME_CONSTANTS } from '../constants/GameConstants';
import { DebugLogService } from '../services/DebugLogService';
import { gameLogger } from '../../utils/GameLogger';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { storage } from '../services/StorageService';
import { ChatHandler } from '../handlers/chat/ChatHandler';
import { toggleAggroRangeOverlay } from '../systems/debug/AggroRangeOverlayState';
import { toggleMeleeConeOverlay } from '../systems/debug/MeleeConeOverlayState';
import { livePlayerPosition, withLivePosition } from '../systems/player/livePlayerPosition';
import { readPlayerVitals, withLiveVitals, writePlayerVitals } from '../systems/player/livePlayerVitals';

export interface DevActionDependencies {
  player: Helix2000Character | null;
  gameWorld: GameScene | null;
  gameInstance: any | null;
  authToken: string | null;
  setPlayer: React.Dispatch<React.SetStateAction<Helix2000Character | null>>;
  setShowCombatLog: React.Dispatch<React.SetStateAction<boolean>>;
  showDevActionLogs: boolean;
  // useDebugConfig exports this as a plain (value: boolean) => void — NOT a
  // React.Dispatch setter — so do not call it with a prev => updater.
  setShowDevActionLogs: (value: boolean) => void;
  characterService: CharacterService;
}

/**
 * The server `spawn_mob` payload for a DevTools spawn: a bare mob id
 * (`'spawn'`) or `{ type, x?, y? }` (`'spawn_mob'`). Null when there is no id.
 * Coordinates are passed only when numeric. The server (GM-gated
 * DebugHandler.handleSpawnMob) uses numeric x/y as given, with no range check,
 * and plants the mob ~80 px to the player's right only when they are absent.
 */
export function spawnMobRequest(data: unknown): { type: string; x?: number; y?: number } | null {
  if (typeof data === 'string') return data ? { type: data } : null;
  const d = (data ?? {}) as { type?: unknown; x?: unknown; y?: unknown };
  if (typeof d.type !== 'string' || !d.type) return null;
  return {
    type: d.type,
    ...(typeof d.x === 'number' ? { x: d.x } : {}),
    ...(typeof d.y === 'number' ? { y: d.y } : {}),
  };
}

export function useDevActions(deps: DevActionDependencies) {
  const {
    player,
    gameWorld,
    gameInstance,
    authToken,
    setPlayer,
    showDevActionLogs,
    characterService
  } = deps;

  const eventBus = EventBus.getInstance();

  const handleDevAction = useCallback((action: string, data?: any) => {
    if (!player || !gameWorld) return;

    if (data !== undefined) {
      gameLogger.debug('dev', `🛠️ Dev Action: ${action}`, data);
    } else {
      gameLogger.debug('dev', `🛠️ Dev Action: ${action}`);
    }

    // Log to Debug Tab if enabled
    if (showDevActionLogs) {
      const logText = data !== undefined
        ? `[DevAction] ${action}: ${typeof data === 'object' ? JSON.stringify(data) : data}`
        : `[DevAction] ${action}`;
      DebugLogService.getInstance().log(logText, 'DevTools');
    }

    switch (action) {
    case 'open_level_editor':
      gameLogger.info('dev', 'Use the in-game editor (Backtick key) instead');
      break;

    case 'heal': {
      // Emit heal intent - NetworkSyncSystem will forward to server if connected
      eventBus.emit(EVENTS.PLAYER_HEAL_INTENT);
      // Also update local state immediately for responsiveness
      setPlayer(prev => prev ? { ...prev, health: prev.maxHealth, mana: prev.maxMana } : null);
      if (player) {
        const { maxHealth, maxMana } = readPlayerVitals(player);
        writePlayerVitals(player, { health: maxHealth, mana: maxMana });
      }
      break;
    }
    case 'mana': {
      eventBus.emit(EVENTS.PLAYER_MANA_INTENT);
      setPlayer(prev => prev ? { ...prev, mana: prev.maxMana } : null);
      if (player) writePlayerVitals(player, { mana: readPlayerVitals(player).maxMana });
      break;
    }
    case 'levelup': {
      eventBus.emit(EVENTS.PLAYER_LEVEL_UP_INTENT);
      setPlayer(prev => prev ? { ...prev, level: prev.level + 1, maxHealth: prev.maxHealth + 50, health: prev.maxHealth + 50 } : null);
      if (player) {
        const maxHealth = readPlayerVitals(player).maxHealth + 50;
        writePlayerVitals(player, { maxHealth, health: maxHealth });
      }
      break;
    }
    case 'addxp': {
      eventBus.emit('player:addXPIntent', { amount: data || 100 });
      setPlayer(prev => prev ? { ...prev, xp: prev.xp + (data || 100) } : null);
      break;
    }
    case 'teleport':
      if (data && typeof data.x === 'number' && typeof data.y === 'number') {
        // Use PlayerManager for teleport (consistent architecture)
        const registry = ServiceRegistry.getInstance();
        const entitySystem = registry.get('EntitySystem');
        entitySystem?.getPlayerManager()?.teleportPlayer(data.x, data.y);
        // Notify server so remote clients receive authoritative teleport updates.
        eventBus.emit(EVENTS.PLAYER_ACTION_INTENT, {
          type: 'teleport',
          payload: { x: data.x, y: data.y }
        });
        // Also update React state
        setPlayer(prev => prev ? { ...prev, position: { x: data.x, y: data.y } } : null);
      }
      break;
    case 'set_class':
      {
        const newClass = data as CharacterClass;
        gameLogger.info('dev', `🔄 Switching class to: ${newClass}`);
        const newCharacter = createHelix2000Character(player.name, newClass);
        newCharacter.id = player.id;
        newCharacter._id = player._id;
        newCharacter.position = livePlayerPosition(player.position);
        setPlayer(newCharacter);
        // Phase H10 Arc λ.9 Stage D-2 (2026-05-10): scene restart via
        // __GAME_HOST__.scenes (SceneRegistry). Phaser scene plugin removed.
        const host = (window as any).__GAME_HOST__;
        const dbChar = characterService.convertGameToDBCharacter(newCharacter);
        const mapSystem = ServiceRegistry.getInstance().get('MapSystem');
        const currentLevelId = mapSystem?.getCurrentLevel()?.id || GAME_CONSTANTS.DEFAULTS.MAP;
        host?.scenes?.start('GameScene', {
          token: authToken,
          characterId: newCharacter._id,
          characterData: dbChar,
          levelId: currentLevelId
        });
      }
      break;

    case 'spawn':
    case 'spawn_mob': {
      // Both go to the server's spawn_mob, the path /spawnmob uses: it plants
      // the mob (beside the player without x/y) and answers in chat. Each used
      // to emit an event with no server effect (plan 13 §6, plan 21 B6).
      const request = spawnMobRequest(data);
      if (request) {
        eventBus.emit(EVENTS.PLAYER_ACTION_INTENT, { type: 'spawn_mob', payload: request });
        ChatHandler.getInstance().systemMessage(`→ /spawnmob ${request.type}`);
      }
      break;
    }

    case 'spawn_test':
      {
        eventBus.emit(EVENTS.PLAYER_ACTION_INTENT, { type: 'spawn_test', payload: { index: data || '0' } });
      }
      break;

    case 'spawn_all_test':
      {
        eventBus.emit(EVENTS.PLAYER_ACTION_INTENT, { type: 'spawn_all_test', payload: {} });
      }
      break;

    case 'clear_mobs':
      // Server map wipe (memory-mode servers): defeats every mob and answers in
      // chat. (The old client-only clear wiped local copies of server mobs.)
      gameLogger.debug('dev', '💀 Clearing all mobs');
      eventBus.emit(EVENTS.PLAYER_ACTION_INTENT, { type: 'debug_mapWipe', payload: {} });
      break;

    case 'randomize_appearance':
      {
        const newAppearance = TextureGenerator.generateRandomAppearance();
        const updatedPlayer = { ...player, appearance: newAppearance };
        setPlayer(updatedPlayer);
        // Use PlayerManager for appearance update
        const registry = ServiceRegistry.getInstance();
        const entitySystem = registry.get('EntitySystem');
        entitySystem?.getPlayerManager()?.updatePlayerAppearance(newAppearance);
        gameLogger.debug('dev', '🎲 Randomized appearance', newAppearance);
      }
      break;

      // --- LEVEL EDITOR ACTIONS ---
    case 'set_spawn':
      {
        const { x, y } = livePlayerPosition(player.position);
        const mapSystem = ServiceRegistry.getInstance().get('MapSystem');
        mapSystem?.setSpawnPoint(0, Math.round(x), Math.round(y));
        gameLogger.debug('dev', `Spawn point set to ${Math.round(x)}, ${Math.round(y)}`);
      }
      break;

    case 'add_platform':
      {
        const { x, y } = livePlayerPosition(player.position);
        const width = data?.width || 100;
        const height = data?.height || 20;
        const type = data?.type || 'normal';
        const color = data?.color || '#FFFFFF';
        const newPlatform = {
          id: `platform_${Date.now()}`,
          x: Math.round(x),
          y: Math.round(y + 50),
          width,
          height,
          type,
          color
        };
        const mapSystem = ServiceRegistry.getInstance().get('MapSystem');
        mapSystem?.addPlatform(newPlatform);
      }
      break;

    case 'delete_last_platform': {
      const mapSystem = ServiceRegistry.getInstance().get('MapSystem');
      mapSystem?.deleteLastPlatform();
      break;
    }

    case 'clear_platforms':
      if (confirm('Are you sure you want to clear all custom platforms?')) {
        const mapSystem = ServiceRegistry.getInstance().get('MapSystem');
        mapSystem?.clearPlatforms();
      }
      break;

    case 'save_level': {
      const mapSystem = ServiceRegistry.getInstance().get('MapSystem');
      mapSystem?.saveLevel().then((success: boolean) => {
        if (success) gameLogger.info('dev', 'Level saved successfully!');
        else gameLogger.error('dev', 'Failed to save level.');
      });
      break;
    }

    case 'toggle_hitboxes': {
      const registry = ServiceRegistry.getInstance();
      const debugOverlay = registry.get('DebugOverlaySystem');
      if (debugOverlay) {
        // DebugOverlaySystem.config.showHitboxes is the SSOT for the toggle
        // state (was previously parked on the now-deleted boot stub's config).
        const currentState = debugOverlay.getConfig().showHitboxes ?? false;
        debugOverlay.toggleHitboxes(!currentState);
      }
      break;
    }

    case 'toggle_velocity': {
      const registry = ServiceRegistry.getInstance();
      const debugOverlay = registry.get('DebugOverlaySystem');
      if (debugOverlay) {
        // Read the live config state and FLIP it — was hard-coded to true
        // (always-on, never toggled off), mirroring toggle_hitboxes now.
        const currentState = debugOverlay.getConfig().showVelocityVectors ?? false;
        debugOverlay.toggleVelocity(!currentState);
      }
      break;
    }

    case 'toggle_physics_state': {
      const registry = ServiceRegistry.getInstance();
      const debugOverlay = registry.get('DebugOverlaySystem');
      if (debugOverlay) {
        const currentState = debugOverlay.getConfig().showPhysicsState ?? false;
        debugOverlay.togglePhysicsState(!currentState);
      }
      break;
    }

    case 'toggle_collision_flash': {
      const registry = ServiceRegistry.getInstance();
      const debugOverlay = registry.get('DebugOverlaySystem');
      if (debugOverlay) {
        const currentState = debugOverlay.getConfig().showCollisionFlash ?? false;
        debugOverlay.toggleCollisionFlash(!currentState);
      }
      break;
    }

    case 'toggle_aggro_range': {
      // Mob aggro-range circles (MobRenderer) — off by default, session-scoped.
      const enabled = toggleAggroRangeOverlay();
      gameLogger.info('debug', `Mob Aggro Range Circles: ${enabled ? 'ON' : 'OFF'}`);
      break;
    }

    case 'toggle_melee_cone': {
      // Melee swing-cone hitbox (MeleeConeOverlayRenderer) — the resolved
      // per-class/per-ability wedge the damage path actually uses. Off by
      // default, session-scoped.
      const enabled = toggleMeleeConeOverlay();
      gameLogger.info('debug', `Melee Swing Cone: ${enabled ? 'ON' : 'OFF'}`);
      break;
    }

    // Cheats - Now routed through PlayerManager
    case 'toggle_god_mode': {
      const registry = ServiceRegistry.getInstance();
      const entitySystem = registry.get('EntitySystem');
      const isGodMode = entitySystem?.getPlayerManager()?.toggleGodMode() ?? false;
      // Persist to storage for UI state
      storage.setBoolean('helix_god_mode', isGodMode);
      break;
    }
    case 'toggle_noclip': {
      const registry = ServiceRegistry.getInstance();
      const entitySystem = registry.get('EntitySystem');
      const isNoclip = entitySystem?.getPlayerManager()?.toggleNoclip() ?? false;
      // Persist to storage for UI state
      storage.setBoolean('helix_noclip', isNoclip);
      ChatHandler.getInstance().systemMessage(`Noclip ${isNoclip ? 'ON' : 'OFF'}`);
      break;
    }
    case 'set_speed_mult': {
      const registry = ServiceRegistry.getInstance();
      const entitySystem = registry.get('EntitySystem');
      entitySystem?.getPlayerManager()?.setSpeedMultiplier(data);
      ChatHandler.getInstance().systemMessage(`Speed ×${data}`);
      break;
    }

    case 'toggle_combat_log':
      deps.setShowCombatLog(prev => !prev);
      break;

    case 'toggle_dev_action_logs':
      // setShowDevActionLogs is a plain (value: boolean) => void (not a
      // functional updater), so the flip reads the current closure value —
      // which is why showDevActionLogs MUST be in the useCallback deps below.
      deps.setShowDevActionLogs(!showDevActionLogs);
      break;

    case 'change_map': {
      gameLogger.info('dev', `🗺️ Switching map to: ${data}`);
      // Phase H10 Arc λ.9 Stage D-2 (2026-05-10): scene restart via
      // __GAME_HOST__.scenes (SceneRegistry).
      const host = (window as any).__GAME_HOST__;
      const dbChar = characterService.convertGameToDBCharacter(withLiveVitals(withLivePosition(player)));
      host?.scenes?.start('GameScene', {
        token: authToken,
        characterId: player._id,
        characterData: dbChar,
        levelId: data
      });
      break;
    }

    case 'chat':
      gameLogger.debug('dev', '🛠️ [DevAction] Processing chat:', { data, type: typeof data });
      if (data && typeof data === 'string') {
        // Emit chat intent - NetworkSyncSystem will forward to server if connected
        eventBus.emit(EVENTS.PLAYER_CHAT_INTENT, { message: data });
        gameLogger.debug('chat', '💬 Chat intent emitted via Dev Action');
      }
      break;

    case 'debug_kernel':
      {
        const kernel = gameWorld.getKernel();
        const handler = ChatHandler.getInstance();
        if (!kernel) {
          handler.systemMessage('❌ No Game Kernel found in current scene.');
          break;
        }
        const ids = kernel.getSystemIds();
        const booted = kernel.getIsBooted();
        handler.systemMessage(`=== Game Kernel Diagnostic (Booted: ${booted}) ===`);
        handler.systemMessage(`Registered Systems (${ids.length}):`);
        ids.forEach((id: string) => handler.systemMessage(`- ${id}`));
      }
      break;
    case 'debug_state':
      {
        // Emit debug state intent - NetworkSyncSystem will provide state info if connected
        eventBus.emit(EVENTS.NET_DEBUG_STATE_INTENT);
        gameLogger.info('network', '🔍 [State Diagnostic] Requested via EventBus');
      }
      break;

    case 'force_sync_stats': {
      // Emit force sync intent - NetworkSyncSystem will handle if connected
      eventBus.emit(EVENTS.NET_FORCE_SYNC_STATS_INTENT);
      gameLogger.info('network', '[CLIENT] Force sync requested via EventBus');
      break;
    }

    case 'connect_server': {
      gameLogger.info('dev', '🔌 [DevAction] Connect to server requested');
      if (authToken && player?._id) {
        // Emit connect intent - NetworkSyncSystem will handle the connection
        eventBus.emit('net:connectIntent', {
          authToken,
          playerId: player._id,
          playerClass: player.class,
          playerName: player.name,
        });
        gameLogger.info('network', '🔗 Connection intent emitted via EventBus');
      } else {
        gameLogger.warn('network', 'Cannot connect: missing auth token or player ID');
      }
      break;
    }

    case 'disconnect': {
      gameLogger.info('dev', '🔌 [DevAction] Disconnect requested');
      // Emit disconnect intent - NetworkSyncSystem will handle disconnection
      eventBus.emit(EVENTS.NET_DISCONNECT_INTENT);
      gameLogger.info('network', '🔌 Disconnect intent emitted via EventBus');
      break;
    }

    case 'toggle_logger':
      // Cycle Logging Modes (Silent -> Minimal -> Gameplay -> Network -> Verbose)
      gameLogger.cycleLogMode();
      break;

    case 'toggle_devtools':
    case 'toggle_editor':
      // These are handled by GameUIOverlay
      break;

    case 'show_floating_text': {
      if (data && gameWorld) {
        const { text, x, y, color = '#ffffff', duration = 2000 } = data;
        const scene = (gameWorld as unknown as { scene?: unknown }).scene;
        const colorInt = parseInt(color.replace('#', ''), 16) || 0xffffff;
        spawnFloatingDamageText(scene ?? gameWorld, {
          x: x || 400,
          y: y || 300,
          text,
          color: colorInt,
          fontSize: 20,
          riseY: 50,
          durationMs: duration,
        });
      }
      break;
    }
    }
  }, [player, gameWorld, gameInstance, authToken, setPlayer, deps.setShowCombatLog, showDevActionLogs, deps.setShowDevActionLogs, characterService]);

  // Allow other components (QAPanel, DevTools) to trigger dev actions via EventBus
  React.useEffect(() => {
    const unsub = eventBus.on('dev:action', (payload: { type: string; data?: any }) => {
      handleDevAction(payload.type, payload.data);
    });
    return unsub;
  }, [handleDevAction]);

  return { handleDevAction };
}
